import { useEffect, useState } from 'react';
import { defaultPauseUntil } from '../Menu/MenuProviderSections/Items/itemPause.helpers';
import {
  ProviderPauseChoice,
  ProviderPauseStart,
  defaultScheduledEnd,
  pauseFromError,
  pauseUntilError,
  pickedPauseEndLabel,
  pickedPauseRangeLabel,
  pickedPauseTimesLabel,
  providerPauseLength,
  scheduledPauseError,
} from './providerPause';

/**
 * The pause length a dialog is building, shared by one provider's pause and
 * Pause All. Starts over each time the dialog opens.
 */
export const useProviderPause = (isOpen: boolean, timeZone?: string) => {
  const [choice, setChoice] = useState<ProviderPauseChoice>(null);
  const [start, setStart] = useState<ProviderPauseStart>('now');
  const [from, setFrom] = useState<Date[]>([]);
  const [until, setUntil] = useState<Date[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setChoice(null);
    setStart('now');
    setFrom([defaultPauseUntil(timeZone)]);
    setUntil([defaultPauseUntil(timeZone)]);
  }, [isOpen, timeZone]);

  /** Keeps the end after the start, so a later start never opens on a 400. */
  const pushUntilPast = (start: Date | undefined) => {
    if (start && (!until[0] || until[0] <= start)) {
      setUntil([defaultScheduledEnd(start)]);
    }
  };

  const changeStart = (value: ProviderPauseStart) => {
    setStart(value);
    if (value !== 'later') return;
    // A dialog left open can hold a start that has gone by since.
    const start = pauseFromError(from[0], timeZone)
      ? defaultPauseUntil(timeZone)
      : from[0];
    setFrom([start]);
    pushUntilPast(start);
  };

  const changeFrom = (value: Date[]) => {
    setFrom(value);
    pushUntilPast(value[0]);
  };

  const picked = choice === 'until' ? until[0] : undefined;
  /** A Custom Time pause set to start later: only then is `pauseFrom` sent. */
  const isScheduled = choice === 'until' && start === 'later';
  const pickedFrom = isScheduled ? from[0] : undefined;

  const rangeLabel =
    picked && pickedFrom
      ? pickedPauseRangeLabel(pickedFrom, picked, timeZone)
      : null;

  return {
    choice,
    setChoice,
    start,
    setStart: changeStart,
    from,
    setFrom: changeFrom,
    until,
    setUntil,
    isScheduled,
    length: providerPauseLength(choice, picked, pickedFrom),
    /** Why `Custom Time` cannot be sent as it is, null when it can. */
    error:
      choice !== 'until'
        ? null
        : isScheduled
        ? scheduledPauseError(pickedFrom, picked, timeZone)
        : pauseUntilError(picked, timeZone),
    /** "Today, 2:00 PM - 3:00 PM" for a scheduled one, for its toast. */
    rangeLabel,
    /** "Pause until 5:00 PM" for the confirm button, null for the other lengths. */
    confirmLabel:
      picked && pickedFrom
        ? `Schedule pause ${pickedPauseTimesLabel(
            pickedFrom,
            picked,
            timeZone
          )}`
        : picked
        ? `Pause until ${pickedPauseEndLabel(picked, timeZone)}`
        : null,
  };
};

export type ProviderPauseState = ReturnType<typeof useProviderPause>;
